import React, { useState, useEffect } from 'react'
import { X, Loader2, AlertCircle } from 'lucide-react'
import { HRDocument } from '@/services/hrService'
import { useDocumentEdit } from '@/hooks/useHRDocuments'
import { DOCUMENT_CATEGORIES } from '@shared'

interface EditDocumentModalProps {
  document: HRDocument | null
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  document,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
  })

  const { editing, editError, updateDocument, clearError } = useDocumentEdit()

  // Reset form when document changes
  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title,
        description: document.description,
        category: document.category,
      })
    } else {
      setFormData({
        title: '',
        description: '',
        category: '',
      })
    }
    clearError()
  }, [document, clearError])

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
    })
    clearError()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!document) return

    const updates = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      category: formData.category.trim(),
    }

    const updatedDocument = await updateDocument(document.id, updates)
    if (updatedDocument) {
      handleClose()
      onSuccess()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  // Use categories from schema
  const categories = DOCUMENT_CATEGORIES

  if (!isOpen || !document) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Edit Document</h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={editing}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Display */}
          {editError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="text-destructive font-medium">{editError}</span>
              </div>
            </div>
          )}

          {/* Title Field */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-foreground mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              disabled={editing}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 bg-background text-foreground"
              placeholder="Enter document title"
            />
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              disabled={editing}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 resize-vertical bg-background text-foreground"
              placeholder="Enter document description"
            />
          </div>

          {/* Category Field */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-foreground mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              disabled={editing}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 bg-background text-foreground [&>option]:bg-background [&>option]:text-foreground"
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Document Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">File:</span> {document.filename || document.title}
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Size:</span> {document.size}
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Type:</span> {document.type}
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Status:</span> {document.status}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={editing}
              className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={editing || !formData.title.trim() || !formData.category.trim()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {editing && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>{editing ? 'Updating...' : 'Update Document'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditDocumentModal 